import React from "react";
import api from "../../lib/api";
import toast from "react-hot-toast";
import { Dialog, DialogTitle, DialogHeader, DialogContent } from "../ui/dialog";
import UpmProductsForm, { UpmProductFormData } from "./UpmProductsForm";

interface EntityProduct {
  id: string;
  name: string;
  mrp: string;
  pack: string;
  mfr: string;
  code: string;
}

interface UpmProductForm {
  sku_name: string;
  flavour?: string;
  mrp_price: number;
  manufacturer_name: string;
  manufacturer_id: number | null;
  salt_name: any[]; // Molecular composition array
  sku_sub_category: string;
  sku_sub_category_id: number | null;
  pack_form: string;
  pack_form_id: number | null;
  drug_form?: string;
  drug_form_id?: number | null;
  schedule: string;
  brand: string;
  brand_id: number | null;
  hsn_name: string;
  hsn_id: number | null;
  cold_storage: string;
  consume_type?: string;
  consume_type_id?: number | null;
  item_size?: string;
  units_in_pack?: number;
  unit_type?: string;
  unit_type_id?: number | null;
  variant?: string;
  chronic_type?: string;
  composition?: string;
  composition_id?: number | null;
  therapeutic_usage?: string;
  therapeutic_usage_id?: number | null;
  tags?: Array<{ id: number; name: string }>;
  variants?: Array<{ category: { id: number; name: string; code: string }; value: { id: number; value: string; code: string; category_id: number } }>;
  image_urls?: string[];
}

interface UpmProduct {
  sku_id: number;
  sku_name: string;
  sku_sub_category?: string;
  brand: string;
  manufacturer_name: string;
  mrp_price: number;
  salt_name?: string;
  strength?: string;
  flavour?: string;
  drug_form?: string;
  item_size?: string;
  unit_type?: string;
  potency?: string;
  pack_form?: string;
  show_in_upm?: boolean;
}

interface CreateProductModalProps {
  product: EntityProduct | null;
  isOpen: boolean;
  onClose: () => void;
  onProductLinked: (entityProductId: string, upmProduct: UpmProduct) => void;
}

const CreateProductModal: React.FC<CreateProductModalProps> = ({
  product,
  isOpen,
  onClose,
  onProductLinked,
}) => {

  // Validation (simple required check)
  const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
  const initalData = {
    sku_name: product?.name || '',
    display_name: product?.name || '', // Initialize with product name
    manufacturer_name: '',
    manufacturer_id: undefined,
    pack_form: '',
    pack_form_id: undefined,
    mrp_price: parseFloat(product?.mrp || '0'),
    brand: '',
    brand_id: undefined,
    sku_sub_category: '',
    sku_sub_category_id: undefined,
    schedule: '',
    hsn_name: '',
    hsn_id: undefined,
    cold_storage: '',
    salt_name: [],
    tags: [],
    variants: [],
    image_urls: [],
  }
  const handleSubmit = (data: Record<string, any>) => {
    if (Object.keys(errors).length === 0) {
      handleSubmitAndLink(data as UpmProductForm);
    }
  };

  const handleSubmitAndLink = async (upmProductForm: UpmProductForm) => {
    const upmProduct = await handleCreateProduct(upmProductForm);
    if(upmProduct) {
      const response = await api.createProductLink(product?.id as string, upmProduct.sku_id.toString());
      if(response.success) {
        toast.success("Product linked successfully");
      } else {
        toast.error(response.message || "Failed to link product");
      }
    }
    onClose();
  };


  const handleCreateProduct = async (upmProductForm: UpmProductForm) => {
    try {
      const response = await api.createUpmProduct(upmProductForm);
      if (response.success) {
        toast.success("Product created successfully");
        onClose();
        return response.data.product;
      } else {
        toast.error(response.message || "Failed to create product");
      }
    } catch (error) {
      console.error("Error in handleCreateProduct:", error);
      toast.error("Error creating product");
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };


  return (
    <Dialog open={isOpen} onOpenChange={handleClose} modal>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto p-0 bg-white">
        <DialogHeader className="p-6 bg-gradient-to-b from-blue-600 to-indigo-600 text-white">
          <DialogTitle>Create UPM Product</DialogTitle>
        </DialogHeader>
        <div className="px-4 pb-4 mt-0">
          <UpmProductsForm
            onSubmit={handleSubmit}
            onCancel={handleClose}
            initialData={initalData as Partial<UpmProductFormData>}
            submitText="Save"
            loading={false}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateProductModal;
